import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MouseEventHandler, ReactNode, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { AppHeader, Footer, Sidebar } from '@verantos-dev/shared-components';

import type { SidebarConfig } from './types';
import { useSidebar } from './useSidebar';

export interface AppPageLayoutProps {
  /**
   * Optional slot for rendering a research assistant component or any other sidebar component.
   * This makes the layout more flexible and presentational.
   */
  researchAssistantSlot?: ReactNode;
  /**
   * Whether the current path is a chat interface.
   * Chat interfaces typically have no bottom margin.
   */
  isChatInterface?: boolean;
  /**
   * Whether side padding should be disabled for the current path.
   */
  disableSidePadding?: boolean;
  /**
   * Sidebar configuration for app-specific behavior (icons, URLs, handlers, etc.)
   */
  sidebarConfig: SidebarConfig;
  /**
   * Array of allowed apps/features to display in the sidebar
   */
  allowedApps?: Array<{
    appId: string;
    appName: string;
    features?: Array<{ featureId: string }>;
    additionalItems?: Array<{ featureId: string }>;
    sectionTitleId?: string;
  }>;
  /**
   * Function to get the section name for the current app.
   * This is app-specific and should be provided by the app.
   */
  getSectionName?: (appId?: string) => string;
  /**
   * Optional app ID for navigation and section name resolution.
   * If not provided, the first allowedApps appId will be used as fallback.
   */
  appId?: string;
  /**
   * The intl message ID for the platform name (e.g., "appHeader.platform.name", "appHeader.platform.name.internal")
   */
  platformNameMessageId: string;
  /**
   * When true, the sidebar section-title labels are omitted (nav items and
   * dividers are kept). Defaults to falsy.
   */
  // TODO: cleanup with research_assistant_gen_3 ff
  hideSectionTitles?: boolean;
}

export const AppPageLayout = ({
  researchAssistantSlot,
  isChatInterface = false,
  disableSidePadding = false,
  sidebarConfig,
  allowedApps,
  getSectionName,
  appId,
  platformNameMessageId,
  hideSectionTitles,
}: AppPageLayoutProps) => {
  const theme = useTheme();
  // Using the navigation object for logo click
  const navigate = useNavigate();

  // Use provided appId or fallback to first allowedApps appId
  const effectiveAppId = appId ?? allowedApps?.[0]?.appId;

  // get sidebar items for the current user
  const sectionName = getSectionName ? getSectionName(effectiveAppId) : '';
  const { sidebarItems, isSidebarOpen } = useSidebar({
    config: sidebarConfig,
    allowedApps,
    hideSectionTitles,
  });

  const onLogoClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();
      if (effectiveAppId) {
        navigate(`/${effectiveAppId}`);
      }
    },
    [effectiveAppId, navigate]
  );

  return (
    <Box
      data-testid="AppWrapperV2"
      sx={{
        width: '100%',
        height: '100%',
        minWidth: '1366px',
        minHight: '768px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <AppHeader
        appSectionName={sectionName}
        platformNameMessageId={platformNameMessageId}
        onLogoClick={onLogoClick}
      />

      <Box
        sx={{
          display: 'flex',
          height: 'calc(100% - 51px)',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Sidebar
          id="appSidebar"
          items={sidebarItems ?? []}
          selectedItem={''}
          isSidebarOpen={isSidebarOpen}
        />

        {researchAssistantSlot}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flexGrow: 1,
            overflowX: 'auto',
          }}
        >
          <Box
            data-testid="appContent"
            sx={{
              marginBottom: isChatInterface ? 0 : '80px',
              px: disableSidePadding ? 0 : `${theme.spacing(6)}`,
              width: '100%',
              // Chat interfaces fill the full available height (Footer is also
              // hidden below). Non-chat pages keep the original content-height
              // behavior so the Footer sits at the bottom of the viewport.
              ...(isChatInterface && {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }),
            }}
          >
            <Outlet />
          </Box>

          {!isChatInterface && <Footer />}
        </Box>
      </Box>
    </Box>
  );
};
